import { useCallback, useEffect, useRef, useState } from 'react';
import { ToolSchema, PendingFunctionCall } from '@/tools/tool-types';
import { executeTool } from '@/tools';
import { a2fStreamer } from '@/services/a2f-streamer';
import logger from '@/lib/logger';

type RealtimeOptions = {
  instructions?: string;
  tools?: ToolSchema[];
  onConnected?: (session: SessionConfig) => void;
  onDisconnected?: () => void;
  onErrorEvent?: (event: Event) => void;
  onMessage?: (event: MessageEvent<any>) => void;
  onResponseAudioDelta?: (delta: string) => void;
  onResponseAudioDone?: () => void;  // Called when audio output completes
  onResponseTranscriptDelta?: (delta: string) => void;
  onResponseDone?: (message: any) => void;
  onErrorMessage?: (message: any) => void;
  onFunctionCallStarted?: (name: string, callId: string) => void;
  onFunctionCallCompleted?: (name: string, callId: string, result: any) => void;
  onFunctionCallError?: (name: string, callId: string, error: string) => void;
};

type ConnectionStatus = 'connecting' | 'connected' | 'error';

export type VadConfig = {
  threshold?: number;
  silenceMs?: number;
  prefixPaddingMs?: number;
};

export type SessionConfig = {
  token: string;
  url: string;
  useCase?: string;
  vad?: VadConfig;
};

export default function useRealtime(options: RealtimeOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const sessionRef = useRef<SessionConfig | null>(null);
  const connectingRef = useRef(false);
  const pendingMessagesRef = useRef<any[]>([]);
  const pendingFunctionCallsRef = useRef<Map<string, PendingFunctionCall>>(new Map());
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const realtimeVoice =
    process.env.NEXT_PUBLIC_REALTIME_VOICE_CHOICE ||
    process.env.AZURE_OPENAI_REALTIME_VOICE_CHOICE ||
    'shimmer';

  const fetchSession = useCallback((): Promise<SessionConfig> => {
    // Azure Functions are served same-origin under the web app's API.
    const sessionUrl = '/api/realtime/session';

    return fetch(sessionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        useCase: process.env.NEXT_PUBLIC_USE_CASE
      })
    })
      .then(async response => {
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to create realtime session');
        }
        return (await response.json()) as SessionConfig;
      });
  }, []);

  const flushPendingMessages = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    while (pendingMessagesRef.current.length > 0) {
      const command = pendingMessagesRef.current.shift();
      wsRef.current.send(JSON.stringify(command));
    }
  }, []);

  // Helper to send commands - defined early so it can be used in handleMessage
  const sendCommandInternal = useCallback((command: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(command));
    } else {
      pendingMessagesRef.current.push(command);
    }
  }, []);

  const handleMessage = useCallback(
    async (event: MessageEvent<any>) => {
      options.onMessage?.(event);
      let data: any;
      try {
        data = JSON.parse(event.data);
      } catch (error) {
        logger.error('Failed to parse realtime message', error);
        return;
      }

      switch (data.type) {
        case 'response.audio.delta':
        case 'response.output_audio.delta':
          options.onResponseAudioDelta?.(data.delta);
          break;

        case 'response.audio.done':
        case 'response.output_audio.done':
          logger.debug('🔊 Response audio done — finalizing audio stream...');
          if (options.onResponseAudioDone) {
            await options.onResponseAudioDone();
          }
          logger.debug('✅ Audio stream finalized');
          break;

        case 'response.audio_transcript.delta':
        case 'response.output_audio_transcript.delta':
          options.onResponseTranscriptDelta?.(data.delta);
          break;

        case 'response.audio_transcript.done':
        case 'response.output_audio_transcript.done':
          logger.info(`🤖 Bot said: "${data.transcript}"`);
          break;

        case 'conversation.item.input_audio_transcription.completed':
          logger.info(`👤 User said: "${data.transcript}"`);
          break;

        case 'response.done':
          logger.debug('🏁 Response done — awaiting audio finalization...');
          if (options.onResponseDone) {
            await options.onResponseDone(data);
          }
          logger.debug('✅ Response done and audio finalized');
          if (pendingFunctionCallsRef.current.size > 0) {
            logger.debug('🧹 Clearing completed function calls');
            pendingFunctionCallsRef.current.clear();
          }
          break;

        case 'response.cancelled':
          logger.info('⛔ Barge-in detected — resetting A2F streamer');
          a2fStreamer.reset();
          break;

        case 'error':
          options.onErrorMessage?.(data);
          break;

        // Function call tracking - when a function call item is added
        case 'response.output_item.added':
          if (data.item?.type === 'function_call') {
            const { call_id, name } = data.item;
            logger.info(`🔧 Tool call started: ${name} (${call_id})`);

            pendingFunctionCallsRef.current.set(call_id, {
              call_id,
              name,
              arguments: '',
              previous_item_id: data.previous_item_id
            });

            options.onFunctionCallStarted?.(name, call_id);
          }
          break;

        // Function call arguments streaming
        case 'response.function_call_arguments.delta':
          {
            const { call_id, delta } = data;
            const pendingCall = pendingFunctionCallsRef.current.get(call_id);
            if (pendingCall) {
              pendingCall.arguments += delta;
              logger.debug(`📝 Tool args delta: ${pendingCall.name}`);
            }
          }
          break;

        // Function call arguments complete - execute the tool
        case 'response.function_call_arguments.done':
          {
            const { call_id, arguments: argsString } = data;
            const pendingCall = pendingFunctionCallsRef.current.get(call_id);

            if (pendingCall) {
              logger.info(`🔧 Executing tool: ${pendingCall.name}`);
              logger.debug(`📋 Tool args: ${argsString}`);

              try {
                const args = JSON.parse(argsString);
                const result = await executeTool(pendingCall.name, args);

                logger.debug(`✅ Tool result:`, result);

                if (result.sessionUpdate) {
                  sendCommandInternal({
                    type: 'session.update',
                    session: { type: 'realtime', ...result.sessionUpdate }
                  });
                }

                const output = result.success
                  ? (typeof result.data === 'string' ? result.data : JSON.stringify(result.data))
                  : `Error: ${result.error}`;

                sendCommandInternal({
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: call_id,
                    output: output
                  }
                });

                sendCommandInternal({ type: 'response.create' });

                logger.info(`✅ Tool completed: ${pendingCall.name}`);
                options.onFunctionCallCompleted?.(pendingCall.name, call_id, result);
              } catch (error) {
                logger.error(`❌ Tool failed: ${pendingCall.name}`, error);
                const errorMessage = error instanceof Error ? error.message : String(error);

                sendCommandInternal({
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: call_id,
                    output: `Error: ${errorMessage}`
                  }
                });

                options.onFunctionCallError?.(pendingCall.name, call_id, errorMessage);
              }
            }
          }
          break;

        case 'input_audio_buffer.speech_started':
          logger.debug(`🎙️ VAD: speech started at ${data.audio_start_ms}ms`);
          break;

        case 'input_audio_buffer.speech_stopped':
          logger.debug(`🎙️ VAD: speech stopped at ${data.audio_end_ms}ms`);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('metro-speech-started'));
          }
          break;

        case 'input_audio_buffer.committed':
          logger.debug(`🎙️ Audio buffer committed (item ${data.item_id})`);
          break;

        case 'session.created':
          logger.debug('[Realtime] Session created');
          break;

        case 'session.updated':
          logger.debug('[Realtime] Session updated');
          break;

        case 'conversation.item.added':
        case 'conversation.item.done':
        case 'response.created':
        case 'response.content_part.added':
        case 'response.content_part.done':
        case 'response.output_item.done':
        case 'rate_limits.updated':
          break;

        default:
          logger.debug(`[Realtime] Unhandled event: ${data.type}`, data);
          break;
      }
    },
    [options, sendCommandInternal]
  );

  const connect = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (connectingRef.current) {
      logger.debug('[Realtime] connect() already in-flight — skipping');
      return;
    }

    if (
      wsRef.current &&
      (wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    connectingRef.current = true;
    try {
      sessionRef.current = await fetchSession();
      logger.debug('[Realtime] Session fetched', { useCase: sessionRef.current.useCase, url: sessionRef.current.url });
      const wsUrl = sessionRef.current.url.replace(/^http/i, 'ws');

      const protocols: string[] = ['realtime'];
      if (sessionRef.current.token) {
        if (sessionRef.current.url.includes('api.openai.com')) {
          protocols.push(`openai-insecure-api-key.${sessionRef.current.token}`);
        } else {
          protocols.push(`bearer,${sessionRef.current.token}`);
        }
      }

      const ws =
        protocols.length > 0 ? new WebSocket(wsUrl, protocols) : new WebSocket(wsUrl);
      wsRef.current = ws;
      setConnectionStatus('connecting');

      ws.addEventListener('open', () => {
        logger.info('[Realtime] Connected');
        setConnectionStatus('connected');
        options.onConnected?.(sessionRef.current!);
        flushPendingMessages();
      });

      ws.addEventListener('message', event => handleMessage(event));

      ws.addEventListener('error', event => {
        logger.error('[Realtime] WebSocket error', event);
        setConnectionStatus('error');
        options.onErrorEvent?.(event);
      });

      ws.addEventListener('close', (event) => {
        logger.warn(`[Realtime] Disconnected (code ${event.code})`);
        setConnectionStatus('error');
        wsRef.current = null;
        sessionRef.current = null;
        options.onDisconnected?.();
      });
    } catch (error) {
      logger.error('[Realtime] Failed to connect', error);
      setConnectionStatus('error');
    } finally {
      connectingRef.current = false;
    }
  }, [fetchSession, flushPendingMessages, handleMessage, options]);

  const sendCommand = useCallback(
    (command: any) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(command));
      } else {
        pendingMessagesRef.current.push(command);
        connect();
      }
    },
    [connect]
  );

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const startSession = useCallback((overrides?: { instructions?: string; tools?: ToolSchema[] }) => {
    const instructions = overrides?.instructions ?? options.instructions;
    const tools = overrides?.tools ?? options.tools;
    const vad = sessionRef.current?.vad;

    const turnDetection = {
      type: 'server_vad' as const,
      threshold: (vad?.threshold != null && Number.isFinite(vad.threshold)) ? vad.threshold : 0.5,
      prefix_padding_ms: (vad?.prefixPaddingMs != null && Number.isFinite(vad.prefixPaddingMs)) ? vad.prefixPaddingMs : 300,
      silence_duration_ms: (vad?.silenceMs != null && Number.isFinite(vad.silenceMs)) ? vad.silenceMs : 500,
    };

    const sessionConfig: any = {
      type: 'realtime',
      audio: {
        input: {
          noise_reduction: { type: 'far_field' },
          turn_detection: turnDetection,
          transcription: { model: 'whisper-1' }
        },
        output: {
          voice: realtimeVoice
        }
      }
    };

    if (instructions) {
      sessionConfig.instructions = instructions;
    }

    if (tools && tools.length > 0) {
      sessionConfig.tools = tools;
      sessionConfig.tool_choice = 'auto';
      logger.info(`🔧 Session configured with ${tools.length} tool(s): ${tools.map((t: ToolSchema) => t.name).join(', ')}`);
    } else {
      sessionConfig.tool_choice = 'none';
      logger.debug('[Realtime] Session configured without tools');
    }

    sendCommand({
      type: 'session.update',
      session: sessionConfig
    });
  }, [realtimeVoice, sendCommand, options.instructions, options.tools]);

  const addUserAudio = useCallback(
    (base64Audio: string) => {
      sendCommand({
        type: 'input_audio_buffer.append',
        audio: base64Audio
      });
    },
    [sendCommand]
  );

  const sendFunctionCallOutput = useCallback(
    (callId: string, output: string) => {
      logger.debug(`📤 Sending function call output for ${callId}`);
      sendCommand({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: output
        }
      });

      sendCommand({ type: 'response.create' });
    },
    [sendCommand]
  );

  /**
   * Trigger the avatar to speak a specific message (e.g. welcome).
   * Sends a user text item asking the model to "say exactly" the text, then response.create.
   */
  const triggerSpokenMessage = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      const prompt = `Say exactly: ${text.trim()}`;
      sendCommand({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: prompt }]
        }
      });
      sendCommand({ type: 'response.create' });
    },
    [sendCommand]
  );

  const cancelAssistantResponse = useCallback(() => {
    sendCommand({ type: 'response.cancel' });
  }, [sendCommand]);

  /**
   * Inject a plain user text message and trigger a response.
   * Unlike triggerSpokenMessage, the AI responds naturally instead of repeating the text verbatim.
   */
  const sendUserText = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      sendCommand({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text: text.trim() }]
        }
      });
      sendCommand({ type: 'response.create' });
    },
    [sendCommand]
  );

  return {
    connectionStatus,
    startSession,
    addUserAudio,
    sendFunctionCallOutput,
    triggerSpokenMessage,
    sendUserText,
    cancelAssistantResponse,
    connect
  };
}